import { TablePaginationConfig } from 'antd';
import { useCallback, useMemo } from 'react';
import { useRequestPromise } from 'hooks/useRequest';
import { getTableChangeParams, SortDirection, SortDirections } from 'utils/tableHelpers';
import {
    AdditionalCharge,
    ListAdditionalChargesParams,
    ListAdditionalChargesSortParams,
} from 'store/invoice-config/types';
import { listAdditionalCharges } from 'store/invoice-config/client';
import useInvoiceChargesParams from './useInvoiceChargesParams';
import { ListPayload } from 'core/http/response';

type AdditionalChargesRequest = (params: ListAdditionalChargesParams) => Promise<ListPayload<AdditionalCharge>>;

const defaultRequest: AdditionalChargesRequest = (params) => listAdditionalCharges(params);

const useInvoiceChargesFilters = (request: AdditionalChargesRequest = defaultRequest) => {
    const { appliedParams, queryInputVal, setArchived, setQueryInputVal, setPage, setSize, setSortState } =
        useInvoiceChargesParams();

    const listRequest = useCallback((params: ListAdditionalChargesParams) => request(params), [request]);

    const { data, isLoading, refetch } = useRequestPromise(() => listRequest(appliedParams), {}, [appliedParams]);

    const { page, size } = appliedParams;

    const handleTableChange = useCallback(
        (antPagination: any, _filters: any, sorter: any) => {
            const raw = getTableChangeParams({ pagination: antPagination, sorter });

            const { page, size, sortParameter, sortDirection } = (raw || {}) as {
                page?: number;
                size?: number;
                sortParameter?: string;
                sortDirection?: SortDirection;
            };

            setPage((p) => (typeof page === 'number' ? page : p));
            setSize((p) => (typeof size === 'number' ? size : p));

            if (!sortParameter || !sortDirection) {
                setSortState({ sort: undefined, sortDirection: undefined });
            } else {
                setSortState({
                    sort: sortParameter as ListAdditionalChargesSortParams,
                    sortDirection: sortDirection as SortDirections,
                });
            }
        },
        [setPage, setSize, setSortState]
    );

    const pagination: TablePaginationConfig = useMemo(
        () => ({
            current: page + 1,
            pageSize: size,
            total: data?.total ?? 0,
            showSizeChanger: true,
            hideOnSinglePage: true,
            position: ['bottomCenter'],
        }),
        [page, size, data?.total]
    );

    return {
        data,
        isLoading,
        refetch,
        handleTableChange,
        pagination,
        appliedParams,
        queryInputVal,
        setArchived,
        setQueryInputVal,
    };
};

export default useInvoiceChargesFilters;
